<template>
  <BriqPanel class="summary">
    <div class="panel-header mb-S">
      <h2>Summary</h2>
    </div>
    <div class="summary-field" data-test-id="booking-number">
      <label class="label">Booking Number</label>
      <div class="value">{{ booking.Number }}</div>
    </div>
    <div class="summary-field" data-test-id="booking-date-of-visit">
      <label class="label">Date of visit</label>
      <div class="value">{{ getDate(booking.StartTime) }}</div>
    </div>
    <div class="summary-section" data-test-id="booking-participants">
      <label class="label">Participants</label>
      <div style="padding-left: 24px">
        <table>
          <tr
            v-for="(p, i) in participantsPerSegment"
            :key="`participant-${i}`"
            v-if="p.Quantity > 0"
          >
            <td>{{ p.NamePlural }}</td>
            <td class="value">{{ p.Quantity }}</td>
          </tr>
          <tr class="total-participants">
            <td>Total</td>
            <td class="value">{{ totalParticipants }}</td>
          </tr>
        </table>
      </div>
    </div>
    <div class="customer" v-if="mainBooker">
      <div class="summary-field two-lines" data-test-id="booking-booker-name">
        <label class="label">Name</label>
        <div class="value">{{ mainBooker.FullName }}</div>
      </div>
      <div
        class="summary-field two-lines"
        v-if="mainBooker.CompanyName"
        data-test-id="booking-booker-company"
      >
        <label class="label">Company</label>
        <div class="value">{{ mainBooker.CompanyName }}</div>
      </div>
      <div class="summary-field two-lines" data-test-id="booking-main-booker-phone-number">
        <label class="label">Phone number</label>
        <div class="value">
          <a v-if="mainBooker.Mobilenumber" :href="`tel:${mainBooker.Mobilenumber}`">
            {{ mainBooker.Mobilenumber }}
          </a>
        </div>
      </div>
      <div class="summary-field two-lines" data-test-id="booking-booker-email">
        <label class="label">E-mail</label>
        <div class="value">
          <a v-if="mainBooker.Email" :href="`mailto:${mainBooker.Email}`">
            {{ mainBooker.Email }}
          </a>
        </div>
      </div>
    </div>
    <div class="summary-field two-lines" v-if="booking.CurrentStatusCode === 'Concept'">
      <label class="label">Preferred date</label>
      <div class="value">{{ getDateTime(booking.ShoppingCartReservationDateTime) }}</div>
    </div>
    <div class="summary-section" data-test-id="booking-attendance-status">
      <div class="flex-between">
        <label class="label">Attendance status</label>
        <div class="value" :class="{ 'bold green': checkedIn }">
          {{ attendanceStatus }}
        </div>
      </div>
    </div>
    <div class="summary-field" data-test-id="booking-visitors-added">
      <label class="label">Visitors added</label>
      <div class="value" :class="{ 'bold green': visitorsComplete }">
        {{ visitorsLength }} of {{ totalParticipants }}
      </div>
    </div>
    <div class="summary-field" data-test-id="booking-paid">
      <label class="label">Paid</label>
      <div class="value" :class="{ 'bold green': fullyPaid }">
        {{ paid }}
      </div>
    </div>
    <div class="summary-field" data-test-id="booking-status">
      <label class="label">Booking status</label>
      <div class="value">{{ booking.CurrentStatusCode }}</div>
    </div>
    <div class="summary-field" data-test-id="booking-origin">
      <label class="label">Booking origin</label>
      <div class="value">{{ booking.Origin }}</div>
    </div>
    <div class="summary-field" data-test-id="booking-date-of-booking">
      <label class="label">Date of booking</label>
      <div class="value">{{ getDateTime(booking.CreatedDatetime) }}</div>
    </div>
    <!-- prettier-ignore -->
    <div v-if="integrationsWithInputFields.length > 0" >
      <template v-for="(integration) in integrationsWithInputFields">
        <b>{{ integration.IntegrationDisplayName }}</b>
        <template v-for="(integrationField) in integrationFieldDefinitionsList.find(y => y.IntegrationCode === integration.IntegrationCode).FieldDefinitions" >
          <div class="summary-field integration-field" v-if="integrationField && integrationField.Datatype === 'String'">
            <label class="label">{{ integrationField.FieldName }}</label>
            <div class="value">
              <BriqInput class="mb-S" v-model="integrationFields[`${integrationField.IntegrationCode}-${integrationField.FieldCode}`]" :disabled="savingIntegrationFields" />
              <button class="save-button" @click="saveIntegrationFields()"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-save"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/><path d="M7 3v4a1 1 0 0 0 1 1h7"/></svg></button>
            </div>
          </div>
          <div v-else class="value">TODO</div>
        </template>
      </template>
    </div>
  </BriqPanel>
</template>

<script>
import { mapGetters } from 'vuex'
import { pipe, prop, either, equals } from 'ramda'
import { sumProp } from '@v/util/briqRamdaExt.js'
import { formatShortDateFromUtc, fromUtc } from '@/helpers/dateTimeFormatter.js'
import { currencyFormatter } from '@/helpers/intlFormatter.js'
import { AttendanceStatus } from '@v/bookings/details/BookingDetailsApiModel.js'
import BusinessFeature from '@v/models/BusinessFeatureEnum.js'

export default {
  name: 'BookingSummaryPanel',
  components: {},
  props: {
    booking: Object,
    mainBooker: Object,
    visitorsLength: { type: Number, required: true },
    amountPaid: { type: Number, required: true },
    totalCostIncludingTax: { type: Number, required: true },
    integrationFieldDefinitionsList: Array,
  },
  data() {
    return {
      savingIntegrationFields: false,
      //prettier-ignore
      integrationFields: Object.fromEntries(
        this.integrationFieldDefinitionsList.flatMap(x => {

          const existingValues = this.booking.IntegrationFields.find(
            y => y.IntegrationCode === x.IntegrationCode,
          )?.Fields

          return x.FieldDefinitions.map(y => {
            const value =
              existingValues?.find(z => z.FieldCode === y.FieldCode)?.Values[0] ||
              y.DefaultValue
            return [`${y.IntegrationCode}-${y.FieldCode}`, value]
          })
        })
      )
    }
  },
  computed: {
    ...mapGetters('briqState', ['businessUnitHasFeature']),
    bookingIsCancelled() {
      return this.booking.CurrentStatusCode === 'Cancelled'
    },
    participantsPerSegment() {
      return this.booking.NumberOfPersonsPerSegment
    },
    totalParticipants() {
      return sumProp('Quantity')(this.participantsPerSegment)
    },
    attendanceStatus() {
      switch (this.booking.AttendanceStatus) {
        case 'CheckedIn':
          return 'Checked in'
        case 'NoShow':
          return 'No show'
        case 'Open':
        default:
          return 'Open'
      }
    },
    integrationsWithInputFields() {
      return this.integrationFieldDefinitionsList?.filter(y => y.FieldDefinitions?.length > 0) || []
    },
    businessAllowsCheckin() {
      return this.businessUnitHasFeature(BusinessFeature.BookingCheckin)
    },
    checkedIn() {
      return this.booking.AttendanceStatus === AttendanceStatus.CheckedIn
    },
    paid() {
      const formatPrice = currencyFormatter(this.$locale, this.$currency).format
      const isInvoice = pipe(
        prop('NextBillingMethod'),
        either(equals('InvoiceNow'), equals('InvoiceAfter')),
      )(this.booking)
      return isInvoice
        ? 'Invoice'
        : `${formatPrice(this.amountPaid)} of ${formatPrice(this.totalCostIncludingTax)}`
    },
    fullyPaid() {
      return this.amountPaid === this.totalCostIncludingTax
    },
    visitorsComplete() {
      return this.visitorsLength === this.totalParticipants
    },
  },
  methods: {
    getDate(dateTimeUtc) {
      return dateTimeUtc ? formatShortDateFromUtc(this.$locale)(this.$timezone)(dateTimeUtc) : null
    },
    getDateTime(dateTimeUtc) {
      return fromUtc()(this.$locale)(this.$timezone)(dateTimeUtc)
    },
    saveIntegrationFields() {
      this.savingIntegrationFields = true
      const integrationObject = {}

      Object.entries(this.integrationFields).forEach(keyValue => {
        const [IntegrationCode, FieldCode] = keyValue[0].split('-')
        const value = keyValue[1]

        if (!integrationObject[IntegrationCode]) {
          integrationObject[IntegrationCode] = { IntegrationCode, Fields: [] }
        }

        const integration = integrationObject[IntegrationCode]

        let field = integration.Fields.find(f => f.FieldCode === FieldCode)

        if (!field) {
          field = { FieldCode, Values: [] }
          integration.Fields.push(field)
        }

        field.Values.push(value)
      })

      const toPost = {
        IntegrationFields: Object.values(integrationObject),
      }

      this.$emit('saveIntegrationFields', toPost)
    },
    updateIntegrationFieldValue(integrationField, value) {
      this.integrationFields.set(
        `${integrationField.IntegrationCode}-${integrationField.FieldCode}`,
        value,
      )
    },
    getIntegrationFieldValue(integrationField) {
      return this.integrationFields.get(
        `${integrationField.IntegrationCode}-${integrationField.FieldCode}`,
      )
    },
  },
}
</script>
<style lang="stylus">
.summary-field.integration-field
  align-items flex-start !important
  white-space nowrap

.summary-field.integration-field label
  padding-top 9px

.summary-field.integration-field .value input
  text-align right !important
  padding-right 41px
</style>
<style scoped lang="stylus">

.save-button
    position absolute
    right 0px
    height 38px
    width 43px
    margin-top -8px
    padding 3px
    outline unset

.save-button svg
    background #008ce8
    color white
    height 100%
    padding 5px 3px
    width 85%
    border-radius 4px

.panel-header
  display flex
  justify-content space-between

.summary-field
  align-items baseline
  position relative

.summary-field .value button
  position absolute
  top 9px

.summary-field, .flex-between
  display flex
  justify-content space-between

.summary-field, .summary-section
  margin-bottom 12px

  .label
    color #798696

.summary-field.two-lines
  display block

.summary table
  width 100%
  font-size 16px
  font-weight 500
  color #4e5a6a

  tr>td
    padding 8px 0

    &.value
      text-align right

.total-participants
  border-top 1px solid black

.booking-date
  font-weight 100

.green
  color #191

.customer
  margin-top 32px
  margin-bottom 44px
</style>
